/* ════ EXPORT — Tasks CSV + Hours by client ════════════════ */
import { state } from './state.js';
import { $, $$ } from './dom.js';
import { catLabel } from './categories.js';
import { fdate } from './utils/date.js';
import { showToast } from './utils/toast.js';
import { matchFilter } from './filtermatch.js';
import { gbp } from './utils/money.js';

const esc = s => String(s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
let mode = 'tasks';

// [key, label, getter] — getter(task, resourceNameMap)
const FIELDS = [
  ['client', 'Client', t => t.client || ''],
  ['team', 'Team', t => t.team || ''],
  ['name', 'Task', t => t.name || ''],
  ['cat', 'Category', t => catLabel(t.cat)],
  ['half', 'Half day', t => t.half || 'none'],
  ['resource', 'Resource', (t, rm) => rm[t.resource] || t.resource],
  ['consultant', 'Consultant', t => t.consultant || ''],
  ['designer', 'Output designer', t => t.designer || ''],
  ['start', 'Start', t => t.start],
  ['end', 'End', t => t.end],
  ['job', 'Job #', t => t.job || ''],
  ['hours', 'Hours', t => Number(t.hours) || 0],
  ['cost', 'Cost (£)', t => Number(t.cost) || 0],
  ['importance', 'Importance', t => t.importance || 'normal'],
  ['completed', 'Complete', t => t.completed ? 'yes' : 'no'],
  ['stDone', 'Subtasks done', t => (t.subtasks || []).filter(s => s.done).length],
  ['stTotal', 'Subtasks total', t => (t.subtasks || []).length],
  ['tags', 'Tags', t => (t.tags || []).join('; ')],
  ['createdBy', 'Added by', t => t.created_by || ''],
  ['notes', 'Notes', t => t.info || ''],
];

const close = () => $('csv-modal').classList.remove('open');

function dateFiltered(tasks) {
  const from = $('csv-from').value, to = $('csv-to').value;
  let t = tasks.slice();
  if (from) t = t.filter(x => x.end >= from);
  if (to) t = t.filter(x => x.start <= to);
  return t;
}
function download(rows, name) {
  const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const a = document.createElement('a');
  a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
  a.download = name; a.click();
}

/* ── Tasks CSV ─────────────────────────────────────────── */
function renderFields() {
  $('csv-fields').innerHTML = FIELDS.map(([k, l]) =>
    `<label class="csv-field"><input type="checkbox" value="${k}" checked> ${l}</label>`).join('');
}
function exportTasks() {
  const chosen = FIELDS.filter(f => $('csv-fields').querySelector(`input[value="${f[0]}"]`).checked);
  if (!chosen.length) { showToast('Pick at least one field'); return; }
  const rm = {}; state.resources.forEach(r => rm[r.id] = r.name);
  const tasks = dateFiltered($('csv-filtered').checked ? state.tasks.filter(matchFilter) : state.tasks)
    .sort((a, b) => a.start.localeCompare(b.start));
  const rows = [chosen.map(f => f[1])];
  tasks.forEach(t => rows.push(chosen.map(f => f[2](t, rm))));
  download(rows, `grantt-${fdate(new Date())}.csv`);
  close(); showToast(`Exported ${tasks.length} tasks ✓`);
}

/* ── Hours by client ───────────────────────────────────── */
function hoursRows() {
  const client = $('hr-client').value;
  return dateFiltered(state.tasks).filter(t => client === 'all' || (t.client || '') === client)
    .sort((a, b) => a.start.localeCompare(b.start));
}
function renderHours() {
  const rows = hoursRows(), total = rows.reduce((s, t) => s + (Number(t.hours) || 0), 0);
  $('hr-results').innerHTML = rows.length
    ? rows.map(t => `<div class="hr-row"><span class="hr-dates">${t.start} → ${t.end}</span><span class="hr-name">${t.client ? '<b>' + esc(t.client) + '</b> — ' : ''}${esc(t.name)}</span><span class="hr-h">${Number(t.hours) || 0}h</span></div>`).join('')
      + `<div class="hr-total"><span>Total — ${rows.length} task(s)</span><span>${total}h</span></div>`
    : '<div class="list-empty">No tasks in range.</div>';
}
function exportHours() {
  const rows = hoursRows(); if (!rows.length) { showToast('Nothing to export'); return; }
  const total = rows.reduce((s, t) => s + (Number(t.hours) || 0), 0);
  const out = [['Client', 'Task', 'Start', 'End', 'Hours']];
  rows.forEach(t => out.push([t.client || '', t.name, t.start, t.end, Number(t.hours) || 0]));
  out.push(['', '', '', 'TOTAL', total]);
  download(out, `hours-${$('hr-client').value}-${fdate(new Date())}.csv`);
  close(); showToast('Hours CSV exported ✓');
}
function populateClients() {
  const cur = $('hr-client').value;
  const clients = [...new Set(state.tasks.map(t => t.client).filter(Boolean))].sort();
  $('hr-client').innerHTML = '<option value="all">All clients</option>' + clients.map(c => `<option value="${esc(c)}">${esc(c)}</option>`).join('');
  if (clients.includes(cur)) $('hr-client').value = cur;
}

/* ── Costs (totals by current filters) ─────────────────── */
const RES_NAME = id => state.resources.find(r => r.id === id)?.name || id || '—';
function costTasks() {
  const base = $('cost-filtered').checked ? state.tasks.filter(matchFilter) : state.tasks;
  return dateFiltered(base).filter(t => Number(t.cost) > 0).sort((a, b) => a.start.localeCompare(b.start));
}
// Group rows for the chosen breakdown → [{ label, count, total }].
function costGroups(rows) {
  const by = $('cost-group').value;
  if (by === 'none') return null;
  const key = t => by === 'client' ? (t.client || '— none —')
    : by === 'team' ? (t.team || '— none —')
    : RES_NAME(t.resource);
  const m = new Map();
  rows.forEach(t => {
    const k = key(t); const g = m.get(k) || { label: k, count: 0, total: 0 };
    g.count++; g.total += Number(t.cost) || 0; m.set(k, g);
  });
  return [...m.values()].sort((a, b) => b.total - a.total);
}
function renderCosts() {
  const rows = costTasks(), total = rows.reduce((s, t) => s + (Number(t.cost) || 0), 0);
  const groups = costGroups(rows);
  const box = $('cost-results');
  if (!rows.length) { box.innerHTML = '<div class="list-empty">No costed tasks in range.</div>'; return; }
  const body = groups
    ? groups.map(g => `<div class="hr-row"><span class="hr-name"><b>${esc(g.label)}</b></span><span class="hr-dates">${g.count} task(s)</span><span class="hr-h">${gbp(g.total)}</span></div>`).join('')
    : rows.map(t => `<div class="hr-row"><span class="hr-dates">${t.start} → ${t.end}</span><span class="hr-name">${t.client ? '<b>' + esc(t.client) + '</b> — ' : ''}${esc(t.name)}</span><span class="hr-h">${gbp(t.cost)}</span></div>`).join('');
  box.innerHTML = body + `<div class="hr-total"><span>Total — ${rows.length} task(s)</span><span>${gbp(total)}</span></div>`;
}
function exportCosts() {
  const rows = costTasks(); if (!rows.length) { showToast('Nothing to export'); return; }
  const total = rows.reduce((s, t) => s + (Number(t.cost) || 0), 0);
  const groups = costGroups(rows);
  const out = groups
    ? [[$('cost-group').value === 'resource' ? 'Resource' : $('cost-group').value === 'team' ? 'Team' : 'Client', 'Tasks', 'Cost (£)'],
       ...groups.map(g => [g.label, g.count, g.total]), ['TOTAL', rows.length, total]]
    : [['Client', 'Team', 'Task', 'Resource', 'Start', 'End', 'Cost (£)'],
       ...rows.map(t => [t.client || '', t.team || '', t.name, RES_NAME(t.resource), t.start, t.end, Number(t.cost) || 0]),
       ['', '', '', '', '', 'TOTAL', total]];
  download(out, `costs-${fdate(new Date())}.csv`);
  close(); showToast('Costs CSV exported ✓');
}

function setMode(m) {
  mode = m;
  $$('.exp-tab').forEach(b => b.classList.toggle('on', b.dataset.mode === m));
  $('exp-tasks').style.display = m === 'tasks' ? '' : 'none';
  $('exp-hours').style.display = m === 'hours' ? '' : 'none';
  $('exp-costs').style.display = m === 'costs' ? '' : 'none';
  if (m === 'hours') { populateClients(); renderHours(); }
  if (m === 'costs') renderCosts();
}

export function initCsv() {
  renderFields();
  $('btn-csv').addEventListener('click', () => { setMode('tasks'); $('csv-modal').classList.add('open'); });
  $('csv-x').addEventListener('click', close);
  $('csv-cancel').addEventListener('click', close);
  $('csv-export').addEventListener('click', () => mode === 'tasks' ? exportTasks() : mode === 'hours' ? exportHours() : exportCosts());
  $('csv-all').addEventListener('click', () => $('csv-fields').querySelectorAll('input').forEach(i => i.checked = true));
  $('csv-none').addEventListener('click', () => $('csv-fields').querySelectorAll('input').forEach(i => i.checked = false));
  $$('.exp-tab').forEach(b => b.addEventListener('click', () => setMode(b.dataset.mode)));
  $('hr-client').addEventListener('change', renderHours);
  $('cost-filtered').addEventListener('change', renderCosts);
  $('cost-group').addEventListener('change', renderCosts);
  ['csv-from', 'csv-to'].forEach(id => $(id).addEventListener('change', () => { if (mode === 'hours') renderHours(); if (mode === 'costs') renderCosts(); }));
}
